import React, { FC, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";

import { brokerApi } from "@/shared/apis";
import { RegisterState, UserState } from "@/shared/domain";
import { useAuth, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import RegisterChooseBrokerDisplay from "./components/RegisterChooseBrokerDisplay/RegisterChooseBrokerDisplay";
import RegisterChooseBrokerSelect from "./components/RegisterChooseBrokerSelect/RegisterChooseBrokerSelect";

export enum RegisterChooseBrokerSteps {
    CHOOSE = "choose",
    DISPLAY = "display",
}

type RegisterChooseBrokerProps = {
    defaultBroker?: string;
    confirmBrokerSelection: (officeId: string) => void;
};

const RegisterChooseBroker: FC<RegisterChooseBrokerProps> = ({ defaultBroker, confirmBrokerSelection }) => {
    const {
        globalState: { officeId },
    } = useRouterHook<RegisterState>();

    const { t } = useTranslation();
    const { userState } = useAuth();
    const [brokerOfficeId, setBrokerOfficeId] = useState(defaultBroker || officeId || "");
    const [step, setStep] = useState(
        defaultBroker ? RegisterChooseBrokerSteps.DISPLAY : RegisterChooseBrokerSteps.CHOOSE,
    );

    const isExistingUser = userState !== UserState.UNKNOWN;

    const { data: brokerData = {} } = brokerApi.useQueryBroker(brokerOfficeId, {
        suspense: false,
    });

    const onSelectBroker = async (selectedOfficeId: string) => {
        log.info("get broker", { officeId: selectedOfficeId });
        setBrokerOfficeId(selectedOfficeId);
        isExistingUser && tagManager.pushAddBrokerSearchEvent();
        setStep(RegisterChooseBrokerSteps.DISPLAY);
    };

    const displayStep = () => {
        switch (step) {
            case RegisterChooseBrokerSteps.CHOOSE:
                return (
                    <RegisterChooseBrokerSelect onSelectBroker={onSelectBroker} filterUserBrokers={isExistingUser} />
                );
            case RegisterChooseBrokerSteps.DISPLAY:
                return (
                    <RegisterChooseBrokerDisplay
                        broker={brokerData}
                        onWrongBroker={() => setStep(RegisterChooseBrokerSteps.CHOOSE)}
                        onConfirmBroker={() => {
                            isExistingUser && tagManager.pushAddBrokerConfirmationEvent();
                            confirmBrokerSelection(brokerData.officeId);
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="title">{t("Registration.ChooseBroker.Title")}</div>
            <div>{displayStep()}</div>
        </>
    );
};

export default RegisterChooseBroker;
